/**
 * Tutorial Service
 *
 * Handles API calls for managing tutorial state and fetching entitlements.
 * Tutorial progress is persisted via user preferences.
 */

import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../utils/api';

/**
 * Get current user's tutorial state from preferences
 * @returns {Promise<Object>} Tutorial state object
 */
export async function getTutorialState() {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/users/me/preferences`,
    { headers }
  );

  return response.data?.tutorial_state || {
    completed_tracks: [],
    completed_steps: {},
    completed_versions: {},
    last_active_track: null,
    last_active_step_id: null,
    dismissed_at: null,
    auto_start_disabled: false,
  };
}

/**
 * Update tutorial state in user preferences
 * @param {Object} tutorialState - Partial tutorial state to update
 * @returns {Promise<Object>} Updated preferences
 */
export async function updateTutorialState(tutorialState) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.put(
    `${apiBase}/users/me/preferences`,
    { tutorial_state: tutorialState },
    { headers }
  );

  return response.data;
}

/**
 * Mark a tutorial track as completed
 * @param {string} track - Track name ('org_admin' or 'ea')
 * @param {string} version - Tutorial version that was completed
 * @returns {Promise<Object>} Updated preferences
 */
export async function completeTutorialTrack(track, version) {
  // First get current state to append to completed_tracks
  const currentState = await getTutorialState();
  const completedTracks = currentState.completed_tracks || [];

  if (!completedTracks.includes(track)) {
    completedTracks.push(track);
  }

  const completedVersions = currentState.completed_versions || {};
  completedVersions[track] = version;

  return updateTutorialState({
    completed_tracks: completedTracks,
    completed_versions: completedVersions,
    last_active_track: null,
    last_active_step_id: null,
  });
}

/**
 * Mark a step as completed
 * @param {string} track - Track name
 * @param {string} stepId - Step ID that was completed
 * @returns {Promise<Object>} Updated preferences
 */
export async function completeStep(track, stepId) {
  const currentState = await getTutorialState();
  const completedSteps = currentState.completed_steps || {};

  if (!completedSteps[track]) {
    completedSteps[track] = [];
  }

  if (!completedSteps[track].includes(stepId)) {
    completedSteps[track].push(stepId);
  }

  return updateTutorialState({
    completed_steps: completedSteps,
    last_active_track: track,
    last_active_step_id: stepId,
  });
}

/**
 * Update the current position in the tutorial
 * @param {string} track - Track name
 * @param {string} stepId - Current step ID
 * @returns {Promise<Object>} Updated preferences
 */
export async function updateTutorialPosition(track, stepId) {
  return updateTutorialState({
    last_active_track: track,
    last_active_step_id: stepId,
  });
}

/**
 * Dismiss the tutorial
 * @returns {Promise<Object>} Updated preferences
 */
export async function dismissTutorial() {
  return updateTutorialState({
    dismissed_at: new Date().toISOString(),
    auto_start_disabled: true,
  });
}

/**
 * Reset tutorial for a specific track or all tracks
 * @param {string|null} track - Track to reset, or null for all
 * @returns {Promise<Object>} Updated preferences
 */
export async function resetTutorial(track = null) {
  if (track) {
    const currentState = await getTutorialState();
    const completedTracks = (currentState.completed_tracks || []).filter(t => t !== track);
    const completedSteps = { ...currentState.completed_steps };
    delete completedSteps[track];
    const completedVersions = { ...currentState.completed_versions };
    delete completedVersions[track];

    return updateTutorialState({
      completed_tracks: completedTracks,
      completed_steps: completedSteps,
      completed_versions: completedVersions,
      last_active_track: track,
      last_active_step_id: null,
    });
  }

  // Reset all
  return updateTutorialState({
    completed_tracks: [],
    completed_steps: {},
    completed_versions: {},
    last_active_track: null,
    last_active_step_id: null,
    dismissed_at: null,
    auto_start_disabled: false,
  });
}

/**
 * Disable auto-start for tutorials
 * @returns {Promise<Object>} Updated preferences
 */
export async function disableAutoStart() {
  return updateTutorialState({
    auto_start_disabled: true,
  });
}

/**
 * Get current organization's package entitlements
 * Used for filtering tutorial steps based on available features
 * @returns {Promise<Object>} Entitlements object
 */
export async function getEntitlements() {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  try {
    const response = await axios.get(
      `${apiBase}/entitlements/current`,
      { headers }
    );

    return response.data;
  } catch (error) {
    console.warn('Failed to fetch entitlements:', error);
    // Return defaults if fetch fails
    return {
      has_active_package: false,
      package_name: null,
      features: {
        teams_enabled: false,
        rbac_enabled: false,
        sso_enabled: false,
        todos_enabled: true,
        sticky_notes_enabled: true,
      },
      components: [],
    };
  }
}

export default {
  getTutorialState,
  updateTutorialState,
  completeTutorialTrack,
  completeStep,
  updateTutorialPosition,
  dismissTutorial,
  resetTutorial,
  disableAutoStart,
  getEntitlements,
};
